import Component from 'flarum/common/Component';
import app from 'flarum/forum/app';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';

export default class CourseDetailPage extends Component {
  static isPage = true;

  oninit(vnode) {
    super.oninit(vnode);
    this.course = null;
    this.articles = [];
    this.progress = null;
    this.loading = true;
    this.purchasing = false;
    this.purchaseMsg = null;
    this.purchaseSuccess = false;
    this.error = null;
    this.completions = {};

    this.loadCourse();
  }

  // ─── 加载课程 ───────────────────────────────────────

  async loadCourse() {
    const id = m.route.param('id');
    if (!id) {
      this.error = '课程 ID 无效';
      this.loading = false;
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const apiUrl = app.forum.attribute('apiUrl');
      const res = await app.request({
        method: 'GET',
        url: `${apiUrl}/taro/cms/courses/${id}`,
      });

      const models = app.store.pushPayload(res);
      this.course = Array.isArray(models) ? models[0] : models;
      this.articles = this.course.articles() || [];
      this.progress = this.course.data.attributes.progress || null;

      this.loadCompletions();
    } catch (e) {
      this.error = e.message || '加载课程失败';
    } finally {
      this.loading = false;
      m.redraw();
    }
  }

  async loadCompletions() {
    if (this.articles.length === 0) return;
    // 暂时留空，后续可通过 API 获取完成状态
  }

  // ─── 购买 ───────────────────────────────────────────

  async handlePurchase() {
    if (this.purchasing) return;
    this.purchasing = true;
    this.purchaseMsg = null;

    try {
      const apiUrl = app.forum.attribute('apiUrl');
      const res = await app.request({
        method: 'POST',
        url: `${apiUrl}/taro/cms/courses/${this.course.id()}/purchase`,
      });

      this.purchaseSuccess = true;
      this.purchaseMsg = res.message || '课程已解锁';

      if (res.data) {
        app.store.pushPayload(res);
      }
    } catch (e) {
      const body = e.response ? JSON.parse(e.response) : {};
      this.purchaseSuccess = false;
      this.purchaseMsg = body.errors?.[0]?.detail || body.message || '购买失败';
    } finally {
      this.purchasing = false;
      m.redraw();
    }
  }

  // ─── 侧边导航栏 ──────────────────────────────

  sidebarNav() {
    const navItems = [
      { route: 'index', icon: 'far fa-comments', label: '全部主题' },
      { route: 'courses', icon: 'fas fa-book-open', label: '课程' },
      { route: 'courseLibrary', icon: 'fas fa-book-reader', label: '课程中心' },
      { route: 'exam', icon: 'fas fa-graduation-cap', label: '考试认证' },
      { route: 'tags', icon: 'fas fa-th-large', label: '标签' },
    ];

    const currentPath = m.route.get();
    const activeItem = navItems.find(item => {
      const itemPath = app.route(item.route).replace(/\/$/, '');
      if (item.route === 'index') return currentPath === '/' || currentPath === '/index' || currentPath === '';
      return currentPath.startsWith(itemPath);
    }) || navItems[0];

    return (
      <nav className="IndexPage-nav sideNav">
        <ul>
          <li className="item-newDiscussion App-primaryControl">
            <Button className="Button Button--primary IndexPage-newDiscussion" icon="fas fa-edit" onclick={() => app.composer.open()}>
              发布主题
            </Button>
          </li>
          <li className="item-nav">
            <div className="ButtonGroup Dropdown dropdown App-titleControl Dropdown--select itemCount{navItems.length}">
              <button className="Dropdown-toggle Button" data-toggle="dropdown">
                <span className="Button-label">{activeItem.label}</span>
                <i className="icon fas fa-sort Button-caret"></i>
              </button>
              <ul className="Dropdown-menu dropdown-menu">
                {navItems.map(item => {
                  const itemPath = app.route(item.route).replace(/\/$/, '');
                  const isActive = currentPath === itemPath || (item.route === 'index' && currentPath === '/');
                  return (
                    <li className={'item-' + item.route + (isActive ? ' active' : '')}>
                      <a className="hasIcon" href={itemPath}>
                        <i className={'icon ' + item.icon + ' Button-icon'}></i>
                        <span className="Button-label">{item.label}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </li>
        </ul>
      </nav>
    );
  }

  // ─── 页面主体内容 ─────────────────────────────

  pageContent() {
    if (this.loading) {
      return <LoadingIndicator />;
    }

    if (this.error) {
      return (
        <div className="CourseDetailPage">
          <button className="CourseDetailPage-back" onclick={() => m.route.set(app.route('courseLibrary'))}>
            <i className="fas fa-arrow-left" /> 返回课程中心
          </button>
          <div className="error-banner">{this.error}</div>
        </div>
      );
    }

    if (!this.course) {
      return (
        <div className="CourseDetailPage">
          <button className="CourseDetailPage-back" onclick={() => m.route.set(app.route('courseLibrary'))}>
            <i className="fas fa-arrow-left" /> 返回课程中心
          </button>
          <div className="error-banner">课程未找到</div>
        </div>
      );
    }

    const course = this.course;
    const cover = course.coverImage();
    const difficulty = course.difficulty() || 'beginner';
    const difficultyLabel = { beginner: '入门', intermediate: '进阶', advanced: '高级' };
    const difficultyIcon = { beginner: 'fas fa-seedling', intermediate: 'fas fa-star', advanced: 'fas fa-fire' };
    const swordCost = course.swordCost() || 0;
    const studyPeriod = course.studyPeriod();
    const articleCount = this.articles.length;
    const progress = this.progress;
    const isFree = swordCost <= 0;
    const isPurchased = this.purchaseSuccess || isFree;

    return (
      <div className="CourseDetailPage">
        {/* Back */}
        <button className="CourseDetailPage-back" onclick={() => m.route.set(app.route('courseLibrary'))}>
          <i className="fas fa-arrow-left" /> 返回课程中心
        </button>

        {/* Hero */}
        <div className="CourseDetailPage-hero">
          {cover ? (
            <img className="CourseDetailPage-hero-cover" src={cover} alt={course.title()} />
          ) : (
            <div className="CourseDetailPage-hero-cover no-cover">
              <i className="fas fa-graduation-cap" />
            </div>
          )}
          <div className="CourseDetailPage-hero-body">
            <h1 className="CourseDetailPage-hero-title">{course.title()}</h1>

            <div className="CourseDetailPage-hero-meta">
              <span><i className={difficultyIcon[difficulty]} /> {difficultyLabel[difficulty] || difficulty}</span>
              <span><i className="fas fa-file-alt" /> {articleCount} 篇文章</span>
              {studyPeriod && <span><i className="fas fa-clock" /> 建议学习 {studyPeriod}</span>}
              {swordCost > 0 && <span><i className="fas fa-shield-alt" /> {swordCost} 宝剑</span>}
              {course.level() && <span><i className="fas fa-layer-group" /> {course.level().name()}</span>}
            </div>

            {course.description() && (
              <div className="CourseDetailPage-hero-desc">{course.description()}</div>
            )}

            {/* Progress bar */}
            {progress && progress.total > 0 && (
              <div className="CourseDetailPage-progress">
                <div className="CourseDetailPage-progress-bar">
                  <div className="CourseDetailPage-progress-fill" style={{ width: Math.min(progress.percent, 100) + '%' }} />
                </div>
                <div className="CourseDetailPage-progress-text">
                  已完成 {progress.completed}/{progress.total} 篇
                  {progress.is_completed && ' ✓ 已学完'}
                </div>
              </div>
            )}

            {/* Purchase panel */}
            {!isPurchased && (
              <div className="CourseDetailPage-purchasePanel">
                <div>
                  <div className="price">价格：{swordCost} <i className="fas fa-shield-alt" /> 宝剑</div>
                  <div className="hint">购买后永久解锁，随时学习</div>
                </div>
                <button className="Button Button--primary CourseDetailPage-purchaseBtn" onclick={() => this.handlePurchase()} disabled={this.purchasing}>
                  {this.purchasing
                    ? <span><i className="fas fa-spinner fa-pulse" /> 处理中...</span>
                    : <span><i className="fas fa-unlock" /> 购买课程</span>}
                </button>
              </div>
            )}

            {this.purchaseMsg && (
              <div className={'message-strip ' + (this.purchaseSuccess ? 'success' : 'error')}>
                <i className={'fas ' + (this.purchaseSuccess ? 'fa-check-circle' : 'fa-exclamation-circle')} />{' '}
                {this.purchaseMsg}
              </div>
            )}

            {isPurchased && !this.purchaseMsg && (
              <div className="status-box success" style="margin-top: 12px;">
                <i className="fas fa-check-circle" />
                {isFree ? '免费课程，直接开始学习' : '已购买，随时学习'}
              </div>
            )}
          </div>
        </div>

        {/* 课程内容列表 */}
        <h2 className="CourseDetailPage-treeTitle">
          <i className="fas fa-list-ol" /> 课程内容
        </h2>

        {this.articles.length > 0 ? (
          <div className="CourseDetailPage-articleList">
            {this.articles.map((article, index) => (
              <div className="CourseDetailPage-articleItem" key={article.id()}>
                <span className="CourseDetailPage-articleItem-index">{index + 1}</span>
                <span className="CourseDetailPage-articleItem-title">{article.title()}</span>
                {(() => {
                  const lc = article.levelCode();
                  const mc = article.moduleCode();
                  const tag = lc && mc ? `${lc}-${mc}` : '';
                  return tag ? <span className="CourseDetailPage-articleItem-tag">{tag}</span> : null;
                })()}
                <span className="CourseDetailPage-articleItem-badges">
                  {article.hasQuiz() && <span className="badge quiz">测验</span>}
                  {article.hasAssignment() && <span className="badge assignment">作业</span>}
                </span>
                <a className="CourseDetailPage-articleItem-link"
                  href={app.route('articleReader', { id: article.id() })}>
                  <i className="fas fa-book-open" /> 阅读
                </a>
              </div>
            ))}
          </div>
        ) : (
          <div className="CourseDetailPage-articleList-empty">
            <i className="fas fa-file-alt" />
            <p>课程内容暂未发布</p>
          </div>
        )}
      </div>
    );
  }

  // ─── View ─────────────────────────────────────

  view() {
    return (
      <div className="IndexPage">
        <div className="container">
          <div className="sideNavContainer">
            {this.sidebarNav()}
            <div className="IndexPage-results sideNavOffset">
              {this.pageContent()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
